import axios from "axios";
import { getVisitorId } from "./visitTracking";

const BASE_URL = process.env.REACT_APP_BASE_URL;

/**
 * Create a PENDING gift card and get back the namespaced payment id + the
 * server-computed amount (never trust a client-computed price for the charge).
 */
export const createGiftCardIntent = (payload) =>
  axios.post(`${BASE_URL}/giftcard/create`, payload).then((r) => r.data);

/**
 * Refresh the delivery details of a PENDING intent (Apple Pay creates the
 * intent before the pay-sheet tap, so late form edits sync through here).
 */
export const updateGiftCardIntent = (payload) =>
  axios.post(`${BASE_URL}/giftcard/update-intent`, payload).then((r) => r.data);

/**
 * Charge a credit card for a gift order (Tranzila, synchronous). The server
 * charges its OWN computed amount for the intent — the card details are the
 * only client input.
 */
export const payGiftCardWithCard = (payload) =>
  axios.post(`${BASE_URL}/giftcard/pay-card`, payload).then((r) => r.data);

/** Tranzila handshake token (same endpoint the main checkout uses). */
export const getApplePayHandshake = (amount) =>
  axios.get(`${BASE_URL}/apple-pay-handshake?amount=${amount}`).then((r) => r.data);

/**
 * Load Tranzila's Apple Pay script (jQuery + thfapplepay.js), once.
 * Resolves when window.hfChargeApple is callable.
 */
let tranzilaAppleLoading = null;
export const loadTranzilaApplePayScript = () => {
  if (window.hfChargeApple) return Promise.resolve();
  if (tranzilaAppleLoading) return tranzilaAppleLoading;
  tranzilaAppleLoading = new Promise((resolve, reject) => {
    const fail = (e) => {
      tranzilaAppleLoading = null;
      reject(e instanceof Error ? e : new Error("script load failed"));
    };
    const jq = document.createElement("script");
    jq.src = "https://code.jquery.com/jquery-3.6.0.min.js";
    jq.async = true;
    jq.onload = () => {
      const s = document.createElement("script");
      s.src = `https://hf.tranzila.com/assets/js/thfapplepay.js?v=${Date.now()}`;
      s.async = true;
      s.onload = () => {
        const nc = document.createElement("script");
        nc.textContent = "var $n = jQuery.noConflict(true);";
        document.body.appendChild(nc);
        if (typeof window.hfChargeApple === "function") resolve();
        else fail(new Error("hfChargeApple missing after load"));
      };
      s.onerror = fail;
      document.body.appendChild(s);
    };
    jq.onerror = fail;
    document.body.appendChild(jq);
  });
  return tranzilaAppleLoading;
};

/**
 * Fire-and-forget gift-flow event log — lands in the SERVER terminal so the
 * owner can watch users move through the flow (tier picks, custom amounts,
 * completed forms) with their session + onboarding identity attached.
 */
const giftDevice = () => {
  try { return window.matchMedia("(max-width: 767px)").matches ? "mobile" : "desktop"; } catch (e) { return ""; }
};

/**
 * Funnel event for the gift-card page: stored server-side (giftflowevents)
 * and mirrored to GA4 as `gift_<event>` with only the non-personal numeric /
 * boolean fields (frames, custom, hasEmail…) — never names, phones or emails.
 */
export const logGiftFlowEvent = (event, data = {}, mode = "") => {
  try {
    axios
      .post(`${BASE_URL}/giftcard/log`, {
        event,
        data,
        mode,
        device: giftDevice(),
        visitorId: getVisitorId(),
        sessionId: localStorage.getItem("sessionId") || "",
        uid: localStorage.getItem("uniqueUserId") || "",
        name: localStorage.getItem("fullName") || "",
        email: localStorage.getItem("userEmail") || "",
      })
      .catch(() => {});
  } catch (e) { /* logging must never break the flow */ }
  try {
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      const params = { device: giftDevice() };
      if (mode) params.mode = mode;
      Object.entries(data || {}).forEach(([k, v]) => {
        if (typeof v === "number" || typeof v === "boolean") params[k] = v;
        else if (k === "method" && typeof v === "string") params[k] = v.slice(0, 40);
      });
      window.gtag("event", `gift_${String(event).slice(0, 35)}`, params);
    }
  } catch (e) { /* analytics must never break the flow */ }
};

/** Public, non-sensitive status for the gift-success page. */
export const getGiftCardStatus = (giftOrderId) =>
  axios.get(`${BASE_URL}/giftcard/status/${giftOrderId}`).then((r) => r.data);

/** Redemption — validate a code at checkout. */
export const validateGiftCard = (code) =>
  axios.post(`${BASE_URL}/giftcard/validate`, { code }).then((r) => r.data);

/** Redemption — write apply intent on a cart. */
export const applyGiftCard = (sessionId, code, frames) =>
  axios.post(`${BASE_URL}/giftcard/apply`, { sessionId, code, frames }).then((r) => r.data);

/** Redemption — clear apply intent. */
export const removeGiftCard = (sessionId) =>
  axios.post(`${BASE_URL}/giftcard/remove`, { sessionId }).then((r) => r.data);

/**
 * Photo order fully paid by a gift card (₪0): create the order like every checkout
 * (NOT_PAID), then the server checks the card covers it, spends the frames and marks
 * it PAID. Resolves to the oid; throws when the server refuses.
 */
export const completeGiftCardOrder = async (orderFields) => {
  const { data: order } = await axios.post(`${BASE_URL}/gama-createorder`, {
    ...orderFields,
    finalAmount: 0,
    orderStatus: "NOT_PAID",
  });
  if (!order || !order.oid) throw new Error("order not created");
  await axios.post(`${BASE_URL}/giftcard/complete-order`, { oid: order.oid });
  return order.oid;
};

/**
 * Kick off a Bit (Gama) payment for a gift order. Mirrors
 * BitPay/index.js handleBitPayment, but with the GC- gift order id + amount.
 * The Gama webhook branches on the GC- prefix to activate the card.
 */
export const startGiftCardBitPayment = async ({ giftOrderId, amount, payerName, payerPhoneNumber }) => {
  const expiresAt = localStorage.getItem("gamaAccessTokenExpiresAt");
  if (!localStorage.getItem("gamaAccessToken") || Number(expiresAt) <= Date.now()) {
    const auth = await axios.post(`${BASE_URL}/gama-login`);
    if (auth.data.status !== 200) throw new Error("Gama auth failed.");
    localStorage.setItem("gamaAccessToken", auth.data.access_token);
    localStorage.setItem("gamaAccessTokenExpiresAt", Date.now() + auth.data.expires_in);
  }

  const session = await axios.post(`${BASE_URL}/gama-session`, {
    gamaAccessToken: localStorage.getItem("gamaAccessToken"),
  });
  if (session.data.status !== 200) throw new Error("Gama session failed.");

  const create = await axios.post(`${BASE_URL}/gama-create-payment`, {
    orderId: giftOrderId,
    amount,
    payerName,
    payerPhoneNumber,
    transactionId: session.data.transactionProcessingId,
    gamaAccessToken: localStorage.getItem("gamaAccessToken"),
    gamaSessionToken: session.data.token,
    transactionEndpoint: session.data.transactionProcessingEndpoint,
  });
  if (create.data.status !== 200) throw new Error("Gama payment create failed.");
  return create.data;
};
